import { useCallback, useEffect, useRef, useState } from 'react';

export const useInfiniteCarousel = (
  totalItems: number,
  intervalMs = 3000
) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [renderIndex, setRenderIndex] = useState(totalItems > 1 ? 1 : 0);
  const [transitionEnabled, setTransitionEnabled] = useState(totalItems > 1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [intervalResetKey, setIntervalResetKey] = useState(0);
  const activeIndexRef = useRef(0);
  const renderIndexRef = useRef(totalItems > 1 ? 1 : 0);
  const isTransitioningRef = useRef(false);

  const runTransition = useCallback(
    (
      nextRenderIndex: number,
      nextActiveIndex: number,
      resetInterval = false
    ): boolean => {
      if (totalItems <= 1 || isTransitioningRef.current) {
        return false;
      }

      isTransitioningRef.current = true;
      renderIndexRef.current = nextRenderIndex;
      activeIndexRef.current = nextActiveIndex;

      setIsTransitioning(true);
      setTransitionEnabled(true);
      setRenderIndex(nextRenderIndex);
      setActiveIndex(nextActiveIndex);

      if (resetInterval) {
        setIntervalResetKey((prevState) => prevState + 1);
      }

      return true;
    },
    [totalItems]
  );

  useEffect(() => {
    setActiveIndex(0);
    setRenderIndex(totalItems > 1 ? 1 : 0);
    setTransitionEnabled(false);
    setIsTransitioning(false);
    activeIndexRef.current = 0;
    renderIndexRef.current = totalItems > 1 ? 1 : 0;
    isTransitioningRef.current = false;

    if (totalItems <= 1) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      setTransitionEnabled(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [totalItems]);

  useEffect(() => {
    if (totalItems <= 1) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      runTransition(
        renderIndexRef.current + 1,
        (activeIndexRef.current + 1) % totalItems
      );
    }, intervalMs);

    return () => {
      window.clearInterval(interval);
    };
  }, [intervalMs, intervalResetKey, runTransition, totalItems]);

  useEffect(() => {
    if (transitionEnabled || totalItems <= 1) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      setTransitionEnabled(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [totalItems, transitionEnabled]);

  const handleTransitionEnd = useCallback((): void => {
    if (totalItems <= 1) {
      return;
    }

    const currentRenderIndex = renderIndexRef.current;

    if (currentRenderIndex === totalItems + 1) {
      renderIndexRef.current = 1;
      setTransitionEnabled(false);
      setRenderIndex(1);
    } else if (currentRenderIndex === 0) {
      renderIndexRef.current = totalItems;
      setTransitionEnabled(false);
      setRenderIndex(totalItems);
    }

    isTransitioningRef.current = false;
    setIsTransitioning(false);
  }, [totalItems]);

  const goToNext = useCallback(
    (resetInterval = true): boolean =>
      runTransition(
        renderIndexRef.current + 1,
        (activeIndexRef.current + 1) % totalItems,
        resetInterval
      ),
    [runTransition, totalItems]
  );

  const goToPrevious = useCallback(
    (resetInterval = true): boolean =>
      runTransition(
        renderIndexRef.current - 1,
        (activeIndexRef.current - 1 + totalItems) % totalItems,
        resetInterval
      ),
    [runTransition, totalItems]
  );

  return {
    activeIndex,
    goToNext,
    goToPrevious,
    isTransitioning,
    renderIndex,
    transitionEnabled,
    handleTransitionEnd,
    hasMultipleItems: totalItems > 1,
  };
};
